import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  getExpandedRowModel,
  ExpandedState,
  Row,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { cn } from '../../../utils/cn';
import { TableHeader } from './components/TableHeader';
import { TableBody } from './components/TableBody';
import {
  TableLoadingState,
  TableLoadingStateProps,
} from './components/TableLoadingState';
import { TableEmptyState } from './components/TableEmptyState';
import { TableErrorState } from './components/TableErrorState';
import { TableContext } from './TableContext';

/**
 * A flexible and feature-rich table component built on top of @tanstack/react-table.
 * Supports sorting, row expansion, custom styling, loading states, and error handling.
 *
 * @template T - The type of data being displayed in the table
 *
 * Features:
 * - Customizable row styling and click handling
 * - Header customization with sticky option
 * - Cell styling
 * - Row expansion with custom content
 * - Sorting (controlled and uncontrolled)
 * - Loading, empty, and error states
 * - Multiple visual variants
 */

/**
 * Column-level options read from a column definition's `meta`.
 */
export interface TableColumnMeta {
  /**
   * Set false to render the column's cells outside the row link from
   * `row.linkTo`, for cells with their own links or controls.
   * @default true
   */
  rowLink?: boolean;
}

/**
 * Interface defining the available table features and their configurations
 */
export interface TableFeatures<T> {
  /**
   * Row customization options for styling and interaction
   */
  row?: {
    /**
     * CSS class name or function returning a class name for row styling
     * @param props.index - The index of the row
     * @param props.row - The row data object
     */
    className?: string | ((props: { index: number; row: Row<T> }) => string);

    /**
     * Click handler for row interactions
     * @param row - The row data object that was clicked
     * @param event - The click, so a handler can honour cmd/ctrl-click and
     *   open its destination in a new tab rather than navigating in place.
     *   With `linkTo` set as well, the handler only receives plain left
     *   clicks: modified clicks and middle-click fall through to the row's
     *   anchor and open its route in a new tab.
     */
    onClick?: (
      row: Row<T>,
      event: React.MouseEvent<HTMLTableRowElement>,
    ) => void;

    /**
     * Whether to add spacing between rows
     * @default true for 'rounded' variant
     */
    spacer?: boolean;

    /**
     * Resolve the internal react-router route a row links to; when set, cell
     * contents render inside real <Link>s so plain clicks SPA-navigate and
     * middle-click / cmd+click open a new tab. Return undefined for rows
     * that must not navigate. Internal routes only (never external URLs);
     * requires a router context. Combined with `onClick`, a plain left click
     * runs the handler instead of navigating, while modified clicks and
     * middle-click still open the route in a new tab. A column whose cells
     * carry their own links or controls opts out of the row link with
     * `meta: { rowLink: false }`, since an anchor cannot nest inside another.
     * Not compatible with `expansion`.
     */
    linkTo?: (row: Row<T>) => string | undefined;

    /**
     * Names the row's link for screen readers when every column opts out of
     * the row link. The link behind the first cell then takes the row's tab
     * stop, and this is what it reads.
     */
    linkLabel?: (row: Row<T>) => string;

    /**
     * Shade even rows (visual display order) for readability on wide tables.
     * @default false
     */
    zebra?: boolean;
  };

  /**
   * Header customization options for the table
   */
  header?: {
    /**
     * CSS class name for header styling
     */
    className?: string;

    /**
     * Whether to hide the header completely
     * @default true for 'nested' variant
     */
    hide?: boolean;

    /**
     * Whether the header should stick to the top while scrolling.
     * Under the 'contained' variant the frame then clips instead of scrolling
     * sideways, so the header can stick to the page; a table that sticks has
     * to fit its width.
     */
    isSticky?: boolean;
  };

  /**
   * Cell customization options
   */
  cell?: {
    /**
     * CSS class name for cell styling
     */
    className?: string;
  };

  /**
   * Row expansion configuration for expandable rows
   */
  expansion?: {
    /**
     * Whether row expansion is enabled
     * @default true when expansion prop is provided
     */
    enabled?: boolean;

    /**
     * Initial expansion state for rows
     * @type {Record<string, boolean>} - Object mapping row keys to expansion state
     */
    defaultExpanded?: Record<string, boolean>;

    /**
     * Render function for expanded content
     * @param props.row - The row data object
     * @param props.isExpanded - Current expansion state
     * @param props.onToggle - Function to toggle expansion state
     */
    renderContent: (props: {
      row: Row<T>;
      isExpanded: boolean;
      onToggle?: () => void;
    }) => React.ReactNode;

    /**
     * Configuration for the expansion toggle control
     */
    toggle?: {
      /**
       * Position of the toggle control
       * @default 'before'
       */
      position?: 'before' | 'after';

      /**
       * Custom render function for the toggle control
       */
      render?: (props: {
        row: Row<T>;
        isExpanded: boolean;
        onToggle: () => void;
      }) => React.ReactNode;
    };

    /**
     * Function to generate accessibility label for toggle control
     */
    toggleLabel?: (props: {
      row: Row<T>;
      isExpanded: boolean;
    }) => React.ReactNode;
  };

  /**
   * Sorting configuration for the table
   */
  sorting?: {
    /**
     * Whether sorting is enabled
     */
    enabled?: boolean;

    /**
     * Default sort configuration for uncontrolled sorting
     * @type {SortingState}
     */
    defaultSortBy?: SortingState;

    /**
     * Current sort configuration for controlled sorting
     * @type {SortingState}
     */
    sortBy?: SortingState;

    /**
     * Callback fired when sorting changes
     * @param sorting - The new sorting state
     */
    onSortingChange?: (sorting: SortingState) => void;
  };
}

/**
 * Main table component props extending TableFeatures
 */
export interface TableProps<T> extends TableFeatures<T> {
  /**
   * Data to be displayed in the table
   * @type {T[]}
   */
  data: T[];

  /**
   * Column definitions
   * @type {ColumnDef<T>[]}
   */
  columns: ColumnDef<T>[];

  /**
   * Additional CSS classes for the table
   * @type {string}
   */
  className?: string;

  /**
   * Visual variant of the table.
   * 'contained' is the platform's list-table look: a rounded, bordered frame
   * with a tinted header row and zebra shading on by default.
   * 'outlined' has the same frame with a white header and zebra off.
   * @type {'rounded' | 'nested' | 'outlined' | 'contained'}
   */
  variant?: 'rounded' | 'nested' | 'outlined' | 'contained';

  /**
   * Horizontal density of cells. 'compact' tightens the space between columns
   * for dense, information-heavy tables; row height is unaffected.
   * @default 'default'
   */
  horizontalDensity?: 'default' | 'compact';

  /**
   * Flag indicating if the table is in a loading state
   * @type {boolean}
   */
  isLoading?: boolean;

  /**
   * Error state of the table
   * @type {Error | null}
   */
  error?: Error | null;

  /**
   * Props for the loading state component
   * @type {Partial<TableLoadingStateProps>}
   */
  loadingProps?: Partial<TableLoadingStateProps>;
}

/**
 * Table component that renders data in a tabular format with various features
 *
 * @example
 * ```tsx
 * <Table
 *   data={users}
 *   columns={columns}
 *   variant="rounded"
 *   sorting={{ enabled: true }}
 *   row={{ onClick: (row) => console.log(row) }}
 * />
 * ```
 */
export function Table<T extends object>({
  // Core props
  data,
  columns,
  className,
  variant = 'rounded',
  horizontalDensity = 'default',
  isLoading,
  error,
  loadingProps,

  // Feature props
  row,
  header,
  cell,
  expansion,
  sorting,
}: TableProps<T>) {
  const [expanded, setExpanded] = useState<ExpandedState>(
    expansion?.defaultExpanded || {},
  );
  // Internal state for uncontrolled sorting
  const [internalSortingState, setInternalSortingState] =
    useState<SortingState>(sorting?.defaultSortBy || []);

  const isControlledSorting = sorting?.onSortingChange !== undefined;

  // Determine current sorting state
  const currentSortingState = isControlledSorting
    ? sorting.sortBy || []
    : internalSortingState;

  // Initialize table
  const table = useReactTable<T>({
    data,
    columns,
    state: {
      sorting: currentSortingState,
      expanded: expanded,
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(currentSortingState) : updater;
      if (isControlledSorting) {
        sorting?.onSortingChange?.(newSorting);
      } else {
        setInternalSortingState(newSorting);
      }
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSorting: sorting?.enabled,
    enableExpanding: expansion?.enabled,
    manualSorting: isControlledSorting,
    // The table never paginates. Left on, every new `data` array queues a page
    // reset that sets state, and a parent that re-renders on each pass, such as
    // a render retried after a suspense, never stops.
    autoResetPageIndex: false,
  });

  const tableContent = () => {
    if (isLoading) {
      return (
        <>
          {!header?.hide && <TableHeader className={header?.className} />}
          <TableLoadingState {...loadingProps} />
        </>
      );
    }

    if (error) {
      return (
        <>
          {!header?.hide && <TableHeader className={header?.className} />}
          <TableErrorState error={error} />
        </>
      );
    }

    if (table.getRowModel().rows.length === 0) {
      return (
        <>
          {!header?.hide && <TableHeader className={header?.className} />}
          <TableEmptyState />
        </>
      );
    }

    return (
      <>
        {!header?.hide && <TableHeader className={header?.className} />}
        <TableBody />
      </>
    );
  };

  row = {
    ...row,
    spacer: row?.spacer ?? variant == 'rounded',
    zebra: row?.zebra ?? variant === 'contained',
  };
  header = {
    hide: variant === 'nested',
    ...header,
  };

  if (expansion) {
    expansion = {
      enabled: true,
      ...expansion,
    };
  }

  const tableElement = (
    <table
      role="table"
      className={cn(
        'min-w-full bg-white text-sm dark:bg-transparent',
        'divide-y divide-gray-200/60 dark:divide-gray-700/60',
        variant === 'rounded' && [
          'border-separate border-spacing-0',
          'rounded-lg',
        ],
        className,
      )}
    >
      {tableContent()}
    </table>
  );

  return (
    <div className="space-y-4">
      <TableContext.Provider
        value={{
          table,
          variant,
          horizontalDensity,
          row,
          cell,
          expansion,
          header,
          sorting,
        }}
      >
        {variant === 'contained' || variant === 'outlined' ? (
          // Frame on a wrapper div: Firefox does not clip table corners
          // against a rounded table element. The frame owns all four edges;
          // the last row drops its border-b (a straight row border would be
          // clipped bare at the rounded bottom corners).
          // A scrolling frame would become the sticky header's scroll
          // container, so a sticky table clips instead.
          header?.isSticky ? (
            <div className="border-container-border rounded-card overflow-clip border">
              {tableElement}
            </div>
          ) : (
            <div className="border-container-border rounded-card overflow-hidden border">
              <div className="min-w-full overflow-x-auto">{tableElement}</div>
            </div>
          )
        ) : (
          tableElement
        )}
      </TableContext.Provider>
    </div>
  );
}
